import { Link } from "react-router-dom";
import { ArrowLeftRight, Ban, ChevronRight, Crown, DollarSign, Lock, Plus, Share2, Shield, Smartphone, Store, Users as UsersIcon } from "lucide-react";
import DeleteAccountRow from "@/pages/profile/DeleteAccountRow";

/**
 * Mobile-parity "YOUR VAULT" crimson card + stacked menu rows shown only
 * on the current user's own profile page.
 */
export default function VaultAndMenu({ me, isAdmin }) {
  return (
    <div className="mt-6 space-y-3" data-testid="profile-vault-and-menu">
      <Link
        to="/coins"
        data-testid="profile-vault-card"
        className="relative block rounded-2xl overflow-hidden bg-gradient-to-br from-rose-900/70 via-rose-800/60 to-rose-950/70 border border-rose-700/40 p-5 hover:from-rose-900/80 hover:to-rose-950/80 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-black/50 border border-white/10 grid place-items-center text-2xl">
            🌕
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] text-rose-200/70 mb-0.5">Your Vault</div>
            <div className="font-display text-3xl text-white leading-none">
              {(me.coins ?? 0).toLocaleString()} <span className="text-lg opacity-80">coins</span>
            </div>
          </div>
          <div className="ml-2 shrink-0 inline-flex items-center gap-1.5 bg-rose-500 hover:bg-rose-400 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
            <Plus size={14} /> Buy
          </div>
        </div>
      </Link>

      <VaultMenuRow to="/vip" Icon={Crown} iconTint="bg-amber-500"
        title="VIP & Badges" subtitle="Monthly Bronze → Boss supporter tiers · resets on the 1st"
        testid="profile-menu-vip" />
      <VaultMenuRow to="/gift-trades" Icon={ArrowLeftRight} iconTint="bg-rose-500"
        title="Gift Trades" subtitle="Accept / send trade offers"
        testid="profile-menu-gift-trades" />
      <VaultMenuRow to="/coins" Icon={Lock} iconTint="bg-rose-500"
        title="Unlock Premium" subtitle="Social Meet + Inbox + more"
        testid="profile-menu-unlock-premium" />
      {isAdmin && (
        <VaultMenuRow to="/admin" Icon={Shield} iconTint="bg-black border border-rose-500"
          title="Admin panel" subtitle="Users · Streams · Inboxes · Broadcast"
          testid="profile-menu-admin" accent />
      )}
      <VaultMenuRow to="/blocked" Icon={Ban} iconTint="bg-zinc-700"
        title="Blocked users" subtitle="Manage who can't reach you"
        testid="profile-menu-blocked" />
      <VaultMenuRow to="/private-groups" Icon={UsersIcon} iconTint="bg-violet-500"
        title="Private Groups" subtitle="Create fan clubs · Share invite links"
        testid="profile-menu-private-groups" />
      <VaultMenuRow to="/earnings" Icon={DollarSign} iconTint="bg-emerald-600"
        title="Earnings & Payouts" subtitle="Get paid for tips & gifts · Stripe payouts"
        testid="profile-menu-earnings" />
      <VaultMenuRow to="/creator-shop" Icon={Store} iconTint="bg-sky-500"
        title="Creator Shop" subtitle="Sell photo sets, videos & 1:1 requests"
        testid="profile-menu-creator-shop" />
      <VaultMenuRow to="/referrals" Icon={Share2} iconTint="bg-rose-600"
        title="Invite Friends" subtitle="Build the Panda Pack — earn badges & featured placement"
        testid="profile-menu-invite-friends" />
      {!window.Capacitor?.isNativePlatform?.() && (
        <a
          href="https://testflight.apple.com/join/392dP3J1"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="profile-menu-testflight"
          className="w-full flex items-center gap-4 p-4 rounded-2xl transition-colors border border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
        >
          <div className="w-11 h-11 rounded-full grid place-items-center shrink-0 bg-zinc-100">
            <Smartphone size={20} className="text-black" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium leading-tight">Get the iPhone app</div>
            <div className="text-[11px] text-zinc-500 leading-tight mt-0.5">Join the myPandaX beta on TestFlight</div>
          </div>
          <ChevronRight size={18} className="text-zinc-500 shrink-0" />
        </a>
      )}
      <DeleteAccountRow />
    </div>
  );
}

function VaultMenuRow({ to, Icon, iconTint, title, subtitle, testid, accent }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-4 p-4 rounded-2xl transition-colors border ${
        accent
          ? "border-rose-500/60 bg-rose-950/20 hover:bg-rose-950/40"
          : "border-white/8 bg-black/40 hover:bg-black/60"
      }`}
    >
      <div className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${iconTint}`}>
        <Icon size={20} className="text-white" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium leading-tight">{title}</div>
        <div className="text-[11px] text-zinc-500 leading-tight mt-0.5 truncate">{subtitle}</div>
      </div>
      <ChevronRight size={18} className="text-zinc-500 shrink-0" />
    </Link>
  );
}
